import * as React from 'react';
import { cn } from '@/lib/utils';

type StackDirection = 'row' | 'column';
type ResponsiveDirection = Partial<{
  xs: StackDirection;
  sm: StackDirection;
  md: StackDirection;
  lg: StackDirection;
  xl: StackDirection;
}>;

interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  spacing?: number;
  direction?: StackDirection | ResponsiveDirection;
}

// Purpose: Resolve responsive direction classes for Stack.
const getDirectionClasses = (direction: StackProps['direction']) => {
  if (!direction || typeof direction === 'string') {
    return direction === 'row' ? 'flex-row' : 'flex-col';
  }

  return [
    direction.xs === 'row' ? 'flex-row' : direction.xs ? 'flex-col' : null,
    direction.sm === 'row' ? 'sm:flex-row' : direction.sm ? 'sm:flex-col' : null,
    direction.md === 'row' ? 'md:flex-row' : direction.md ? 'md:flex-col' : null,
    direction.lg === 'row' ? 'lg:flex-row' : direction.lg ? 'lg:flex-col' : null,
    direction.xl === 'row' ? 'xl:flex-row' : direction.xl ? 'xl:flex-col' : null,
  ]
    .filter(Boolean)
    .join(' ');
};

// Purpose: Provide a consistent layout stack with gap-based spacing.
export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  ({ spacing = 0, direction = 'column', className, style, ...props }, ref) => {
    const gapValue = Math.max(0, Math.floor(spacing)) * 4;
    return (
      <div
        ref={ref}
        className={cn('flex', getDirectionClasses(direction), className)}
        style={{ ...style, gap: gapValue }}
        {...props}
      />
    );
  }
);

Stack.displayName = 'Stack';
